// Pure TypeScript. No React. No imports from features or services.
//
// THE ANIMATION SYSTEM (LIVEMEET-GUIDE §2)
// Two spring presets, used everywhere, nothing else. Consistency across the
// whole app is what reads as "one crafted product". `layoutId` (used at call
// sites) provides shared-element continuity for the iOS-style morphs.

import type { Transition, Variants } from 'framer-motion';

/**
 * SNAPPY — stiffness ~400, damping ~30. Fast settle, minimal overshoot.
 * For anything the user directly touches: buttons, toggles, tile-focus,
 * press states, the date-strip settle.
 */
export const SNAPPY: Transition = {
  type: 'spring',
  stiffness: 400,
  damping: 30,
  mass: 0.8,
};

/**
 * GENTLE — stiffness ~200, damping ~25. More travel, softer landing.
 * For anything that happens *to* the screen: page/route transitions, cards
 * entering, modals, the lobby countdown, a tile scaling in as it joins.
 */
export const GENTLE: Transition = {
  type: 'spring',
  stiffness: 200,
  damping: 25,
  mass: 1,
};

/**
 * CALM — deliberately slower, for destructive / emotionally-loaded moments
 * (ending a call, muting someone). Haste in the UI during a high-stakes
 * moment feels wrong even if no one can articulate why (§2).
 */
export const CALM: Transition = {
  type: 'spring',
  stiffness: 140,
  damping: 26,
  mass: 1,
};

/** Standard non-spring easing for the rare timed transition. */
export const EASE_OUT = [0.16, 1, 0.3, 1] as const;

// ────────────────────────────────────────────────────────────────────────────
// Route-level transitions stay restrained: fade + 8–12px rise, ~250–300ms.
// A heavy page-level animation fighting LiveKit's internal re-renders is an
// avoidable source of jank — personality lives in micro-interactions, not
// page wipes.
// ────────────────────────────────────────────────────────────────────────────

export const routeTransition: Variants = {
  initial: { opacity: 0, y: 10 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.28, ease: EASE_OUT },
  },
  exit: {
    opacity: 0,
    y: -8,
    transition: { duration: 0.2, ease: EASE_OUT },
  },
};

/** Cards entering with a soft scale — the Gentle spring. */
export const cardEnter: Variants = {
  initial: { opacity: 0, y: 12, scale: 0.98 },
  animate: { opacity: 1, y: 0, scale: 1, transition: GENTLE },
};

/**
 * Staggered field cascade — used by the Creation Widget and the post-call
 * Notes form, so the two screens share one system-wide entrance rhythm.
 */
export const cascadeContainer: Variants = {
  initial: {},
  animate: {
    transition: { staggerChildren: 0.055, delayChildren: 0.04 },
  },
};

export const cascadeItem: Variants = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0, transition: GENTLE },
};

/**
 * Reaction float — physics-based, not a straight linear rise. Slight
 * horizontal drift + rotation + fade; real objects don't move in perfect
 * lines and that imperfection reads as crafted (§4.10).
 */
export function reactionFloat(driftX: number, rotate: number): Variants {
  return {
    initial: { opacity: 0, y: 0, scale: 0.6 },
    animate: {
      opacity: [0, 1, 1, 0],
      y: [0, -60, -150, -240],
      x: [0, driftX * 0.4, driftX * 0.8, driftX],
      rotate: [0, rotate * 0.5, rotate],
      scale: [0.6, 1.1, 1, 0.9],
      transition: { duration: 2.4, ease: 'easeOut', times: [0, 0.15, 0.6, 1] },
    },
  };
}
